import { useEffect, useState, useCallback, useRef } from 'react';

import { getAxiosErrorData } from '@/api/http';
import { getUserByMagicLink, confirmAccountByMagicLink } from '@/api/user';
import { LOGIN, CANDIDATES, MESSAGES } from '@/constants/routes';
import { useApiMutation } from '@/hooks/api';
import { roleFlagsFromRole } from '@/hooks/useCurrentUser';
import { useUrlState } from '@/hooks/useUrlState';
import { useAppDispatch } from '@/store/hooks';
import { login } from '@/store/userSlice';
import {
  getLocalStorageItem,
  removeLocalStorageItem,
  storeAccessToken,
} from '@/utils/localStorage';
import {
  isMagicLinkMfaChallenge,
  type MagicLinkMfaBootstrap,
} from '@/utils/notificationDeepLink';
import { isRecord } from '@/utils/typeGuards';

import type { AuthTokenResponse } from '@/api/user';
import type { AvailableClientProfile, User } from '@/types/user';

type MagicLinkSearchParams = Record<string, string>;

function isAvailableClientProfile(
  value: unknown,
): value is AvailableClientProfile {
  return (
    isRecord(value) &&
    typeof value.id === 'string' &&
    typeof value.company_id === 'string' &&
    typeof value.company_name === 'string' &&
    typeof value.department === 'string'
  );
}

type ProfileSelectionErrorBody = {
  client_profile_id?: string;
  available_profiles?: AvailableClientProfile[];
};

const paramsToObject = (
  entries: Iterable<[string, string]>,
): MagicLinkSearchParams => {
  const result: MagicLinkSearchParams = {};
  for (const [key, value] of entries) {
    result[key] = value;
  }
  return result;
};

function readProfileSelectionError(
  error: unknown,
): ProfileSelectionErrorBody | null {
  const errorBody = getAxiosErrorData(error) as
    { errors?: { errors?: ProfileSelectionErrorBody } } | undefined;
  return errorBody?.errors?.errors ?? null;
}

function buildRedirectLink(
  redirectPathname: string,
  params: MagicLinkSearchParams,
): string {
  if (params.signup === 'true') {
    return '/dashboard';
  }

  if (redirectPathname === CANDIDATES) {
    let link = `${redirectPathname}?filter=in-process`;
    if (params.stage) {
      link += `&stage=${params.stage}`;
    }
    if (params.interview_id) {
      link += `&interview_id=${params.interview_id}`;
    }
    return link;
  }

  if (redirectPathname === MESSAGES) {
    if (params.user_id && params.interview_id) {
      return `${redirectPathname}/${params.user_id}/${params.interview_id}`;
    }
    return redirectPathname;
  }

  if (Object.keys(params).length) {
    return (
      `${redirectPathname}?` +
      Object.keys(params)
        .map((key) => key + '=' + params[key])
        .join('&')
    );
  }

  return redirectPathname;
}

interface UseMagicLinkProps {
  setShowProfileSelector: (show: boolean) => void;
  setAvailableProfiles: (profiles: AvailableClientProfile[]) => void;
}

export function useMagicLink({
  setShowProfileSelector,
  setAvailableProfiles,
}: UseMagicLinkProps) {
  const { history } = useUrlState();
  const dispatch = useAppDispatch();

  const pathname = getLocalStorageItem('pathname');
  const search = getLocalStorageItem('search');
  const [magicLinkToken, setMagicLinkToken] = useState<string | null>(null);
  const [isSignup, setIsSignup] = useState(false);
  const [urlParams, setUrlParams] = useState<MagicLinkSearchParams | null>(
    null,
  );
  const initialLoginStartedRef = useRef(false);

  const handleSuccessfulLogin = useCallback(
    (
      auth: AuthTokenResponse,
      params: MagicLinkSearchParams,
      redirectPathname: string,
    ) => {
      storeAccessToken(auth.access_token ?? '');

      dispatch(
        login({
          user: auth.user as User,
          access_token: auth.access_token ?? '',
          client_profile_id: auth.client_profile_id,
        }),
      );

      if (auth.user) {
        const loggedInUser = auth.user;
        const { isRecruiter } = roleFlagsFromRole(loggedInUser.role);
        const intercomId = import.meta.env.VITE_INTERCOM_ID ?? '';
        localStorage.setItem('intercomID', intercomId);
        const intercom = (
          window as Window & { Intercom?: (...args: unknown[]) => void }
        ).Intercom;
        if (intercom) {
          intercom('boot', {
            app_id: intercomId,
            email: loggedInUser.email,
            name: loggedInUser.name,
            user_id: loggedInUser.id,
            user_role: loggedInUser.role,
            user_has_default_tier: isRecruiter
              ? loggedInUser.has_default_tier
              : false,
          });
        }
      }

      history.push(buildRedirectLink(redirectPathname, params));
      removeLocalStorageItem('pathname');
      removeLocalStorageItem('search');
    },
    [dispatch, history],
  );

  const handleLoginError = useCallback(
    (error: unknown) => {
      const errorData = readProfileSelectionError(error);
      if (
        errorData?.client_profile_id === 'Profile selection required' &&
        errorData.available_profiles
      ) {
        setAvailableProfiles(
          errorData.available_profiles.filter(isAvailableClientProfile),
        );
        setShowProfileSelector(true);
        return;
      }

      history.push(LOGIN);
      removeLocalStorageItem('pathname');
      removeLocalStorageItem('search');
    },
    [history, setAvailableProfiles, setShowProfileSelector],
  );

  const loginMutation = useApiMutation<
    AuthTokenResponse,
    { magic_link: string; isSignupFlow: boolean }
  >({
    mutationFn: async ({ magic_link, isSignupFlow }) => {
      const loginFn = isSignupFlow
        ? confirmAccountByMagicLink
        : getUserByMagicLink;
      return loginFn(magic_link);
    },
    silent: true,
    onSuccess: (res) => {
      const redirectPathname = pathname || '/dashboard';
      const params = urlParams ?? {};
      const nextPath = buildRedirectLink(redirectPathname, params);

      if (isMagicLinkMfaChallenge(res) && res.mfa_token) {
        const bootstrap: MagicLinkMfaBootstrap = {
          mfaToken: res.mfa_token,
          mfaMethod: res.mfa_method,
          mfaSelectionRequired: Boolean(res.mfa_selection_required),
          userEmail: res.user_email,
          nextPath,
        };
        removeLocalStorageItem('pathname');
        removeLocalStorageItem('search');
        history.push(LOGIN, { magicLinkMfa: bootstrap });
        return;
      }

      if (!res.access_token || !res.user) {
        handleLoginError(new Error('Invalid magic link response'));
        return;
      }

      handleSuccessfulLogin(res, params, redirectPathname);
    },
    onError: handleLoginError,
  });
  const { mutate: loginWithMagicLink } = loginMutation;

  const profileLoginMutation = useApiMutation<
    AuthTokenResponse,
    { profileId: string }
  >({
    mutationFn: async ({ profileId }) => {
      if (!magicLinkToken) {
        throw new Error('Missing magic link token');
      }
      const loginFn = isSignup ? confirmAccountByMagicLink : getUserByMagicLink;
      return loginFn(magicLinkToken, profileId);
    },
    silent: true,
    onSuccess: (data) => {
      setShowProfileSelector(false);
      handleSuccessfulLogin(data, urlParams ?? {}, pathname || '/dashboard');
    },
    onError: handleLoginError,
  });

  const handleProfileSelect = (profileId: string) => {
    profileLoginMutation.mutate({ profileId });
  };

  const handleCancel = () => {
    history.push(LOGIN);
    removeLocalStorageItem('pathname');
    removeLocalStorageItem('search');
  };

  useEffect(() => {
    if (pathname && search) {
      const urlSearchParams = new URLSearchParams(search);
      const params = paramsToObject(urlSearchParams.entries());
      setUrlParams(params);

      if (params.magic_link) {
        setMagicLinkToken(params.magic_link);
        const signupFlow = params.signup === 'true';
        setIsSignup(signupFlow);

        // Do not logout before magic-link login — MFA challenges must not wipe
        // an existing session, and failure paths clear state explicitly.
        if (!initialLoginStartedRef.current) {
          initialLoginStartedRef.current = true;
          loginWithMagicLink({
            magic_link: params.magic_link,
            isSignupFlow: signupFlow,
          });
        }
      } else {
        history.push(LOGIN);
        removeLocalStorageItem('pathname');
        removeLocalStorageItem('search');
      }
    } else {
      history.push(LOGIN);
    }
  }, [pathname, search, history, loginWithMagicLink]);

  return {
    handleProfileSelect,
    handleCancel,
    loading: loginMutation.isPending || profileLoginMutation.isPending,
  };
}
